import { call, put, select, takeLatest } from 'redux-saga/effects'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { getPcrIdSelector } from '../../home/selectors'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { IPcr } from '../../pcr/store/pcr.types'
import { getServiceIdSelector } from '../../user/selectors/getServiceId.selector'
import { ZollSupportTicketConstants } from '../store/zollSupportTicket.constants'
import { ZollSupportTicketService } from '../services/zollSupportTicket.service'
import {
  IFile,
  IZollSupportTicketRequest,
  IZollSupportTicketResponse,
  IZollSupportTicketState,
} from '../store/zollSupportTicket.types'
import { getZollSupportTicketState } from '../selectors/zollSupportTicket.selector'
import { setIsLoading, setTicketId } from '../store/zollSupportTicket.actions'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import Logger from '../../../services/logger.service'

export function* createZollSupportTicket() {
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrID: string = yield select(getPcrIdSelector)
  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
  const zollSupportTicketState: IZollSupportTicketState = yield select(getZollSupportTicketState)
  const { emailAddress, phoneNumber, questionOrProblem, preferredContactMethod } = zollSupportTicketState
  try {
    let pcrDoc: IPcr | null = null
    if (pcrID) {
      const pcrDocsService = new PcrDocsService(serviceId)
      pcrDoc = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
    }
    const zollSupportTicketService = new ZollSupportTicketService(authenticationJwt as string)

    const infoFile: IFile = {
      name: 'info.json',
      content: {
        emailAddress,
        phoneNumber,
        questionOrProblem,
        preferredContactMethod,
        insideOfPcr: pcrDoc !== null,
      },
    }

    const pcrFile: IFile = {
      name: 'pcr.json',
      content: {
        ...pcrDoc,
      },
    }

    const files: IFile[] = [infoFile]
    if (pcrDoc) files.push(pcrFile)

    const zollSupportTicketRequest: IZollSupportTicketRequest = {
      emailAddress,
      phoneNumber,
      questionOrProblem,
      preferredContactMethod,
      files,
    }

    const zollSupportTicketResponse: IZollSupportTicketResponse = yield call(
      [zollSupportTicketService, zollSupportTicketService.createZollSupportTicket],
      zollSupportTicketRequest
    )

    if (zollSupportTicketResponse) {
      yield put(setIsLoading(false))
      if (zollSupportTicketResponse.status === 200 && zollSupportTicketResponse.data) {
        yield put(setTicketId(zollSupportTicketResponse.data.ticketId))
        Logger.info(
          `ZollSupportTicket: createZollSupportTicket success, ticketId: ${zollSupportTicketResponse.data.ticketId}`
        )
      } else {
        zollSupportTicketResponse.failure = true
        if (zollSupportTicketResponse.messages?.length) {
          const index = zollSupportTicketResponse.messages.findIndex(
            (x) => x.status === zollSupportTicketResponse.status
          )
          zollSupportTicketResponse.responseMessage = zollSupportTicketResponse.messages[index]?.message
        } else {
          zollSupportTicketResponse.responseMessage =
            'There was an error creating a support ticket. Please contact customer support for further assistance.'
        }
        yield put(handleApiError(zollSupportTicketResponse))
      }
    }
  } catch (e) {
    yield put(setIsLoading(false))
    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: `An error occurred while creating zoll support ticker. Cause: ${e}`,
        },
      ],
    }
    yield put(handleApiError(errorResponse))
  }
}

export function* createZollSupportTicketSaga() {
  yield takeLatest(
    [ZollSupportTicketConstants.CREATE_ZOLL_SUPPORT_TICKET],
    performWithMutexHOG,
    createZollSupportTicket
  )
}
